import { zodResolver } from "@hookform/resolvers/zod";
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Stack } from "@mui/system";

import { useBlocker, useNavigate, useParams } from "react-router-dom";

import { Node, useReactFlow } from "@xyflow/react";

import { PhaseData } from "@/pages/Private/pages/Courses/components/Editor/NodeDataTyps";

import {
	useGetCourseQuery,
	useGetCourseVersionsQuery,
	useGetTagsQuery,
	useUpdateCoursesMutation,
} from "@/pages/Private/redux/courses/courses.api";

import { InputField } from "@/components/InputField/InputField";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { ImageUploader } from "@/components/ImageUploader/ImageUploader";

import { usePageTitle } from "@/utils/usePageTitle";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import PhaseEditor from "@/pages/Private/pages/Courses/components/Editor/PhaseEdit";

import { VideoUploader } from "@components/VideoUploader/VideoUploader";

import { ScenarioGraphData } from "@/pages/Private/pages/Courses/components/Editor/ScenarioGraph";

import { languages } from "@/constants/languages";

import { UserLanguage } from "@/enum/user.enum";

import { Modal } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import ContentModal from "../../Course/components/ContentModal";
import { EMPTY_COURSE_SCENARIO, IStageContentInfo } from "../constants/emptyCourseScenario";
import { FileAssignment } from "../enums/FileAssignment.enum";
import { CourseTag, CourseVersion, UpdateCourses, UpdateCoursesSchema } from "../schema/courses";
import CustomJsonEditor from "./CustomJsonEditor";
import { EditorRightPanel } from "./Editor/EditorRightPanel";
import { PublishStatus, usePublishControls } from "./Editor/PublishControls";
import { IFileInfo } from "./FilesUploader";
import LanguageSelect from "../../Account/components/LanguageSelect";

export const UpdateCoursesForm: FunctionComponent = () => {
	const { t } = useTranslation();

	const ts = useCallback((key: string) => t(`courses.${key}`), [t]);

	usePageTitle(ts("update.title"));
	const { id = 0 } = useParams();
	const navigate = useNavigate();

	const { deleteElements } = useReactFlow();

	const { data, isFetching, isLoading: isLoadingCourse, refetch } = useGetCourseQuery(+id);
	// Mirrors the form's dirty flag for effects declared above `useForm` — a dirty editor
	// is the author mid-thought, and no cache refresh may overwrite it.
	const dirtyRef = useRef(false);
	/**
	 * The editor works on the draft when there is one — the rolling copy every save
	 * rewrites. The course still carries the published content, which is what participants
	 * play, so opening the editor on it would silently discard unpublished work the moment
	 * anything was saved.
	 */
	const {
		data: courseVersions,
		isLoading: isLoadingVersions,
		refetch: refetchVersions,
	} = useGetCourseVersionsQuery(+id);
	/**
	 * The draft revision the form was loaded from (BCM360-383). Sent with every save, so a draft
	 * someone else saved meanwhile — another tab, or an AI client over MCP — is not overwritten
	 * silently: the API refuses with 409 STALE_DRAFT and the author decides.
	 */
	const [baseRevision, setBaseRevision] = useState<number>();
	const [staleSave, setStaleSave] = useState<UpdateCourses | null>(null);
	// The canvas builds its nodes once; "Neu laden" remounts it once the newer draft is loaded.
	const [editorKey, setEditorKey] = useState(0);
	const reloadPending = useRef(false);
	// Bumped by "Neu laden": the refused save already refreshed the cache, so the newer draft
	// is no new object and would not re-initialise the form by itself.
	const [reloadNonce, setReloadNonce] = useState(0);
	const publishing = usePublishControls(+id, data?.status, data?.publishedVersionId, () =>
		refetch()
	);

	/**
	 * Loads an older version into the editor to look at.
	 *
	 * Deliberately only into the form: the stored draft is whatever was last saved, and it
	 * stays that way until the author saves this. So looking at a version costs nothing and
	 * is undone by leaving without saving — which is also why it marks the form dirty, as
	 * what is on screen no longer matches what the server holds.
	 */
	const handleViewVersion = (version: CourseVersion) => {
		const json = (version.json as Record<string, unknown>) ?? {};

		setCurrentFormState((previous) => ({
			...previous,
			// The snapshot's own values, nulls included: a version whose description was
			// empty shows empty, not whatever happens to be on screen — falling back mixed
			// two versions into one nobody ever saved. Only the name keeps a fallback, as
			// the one field the form refuses to submit blank.
			name: version.name ?? previous?.name,
			description: version.description ?? "",
			forWhom: version.forWhom ?? "",
			language: version.language ?? previous?.language,
			json: Object.keys(json).length ? json : EMPTY_COURSE_SCENARIO,
		}));
		// Dirty on purpose: the editor now holds something the server does not, which is
		// what "unsaved" means — and what stops Publish from offering to push the draft
		// still sitting on the server instead of the version now on screen.
		setValue("json", Object.keys(json).length ? json : EMPTY_COURSE_SCENARIO, {
			shouldDirty: true,
		});
		setSelectedNode(null);
	};
	const draft = (courseVersions ?? []).find((version) => version.isDraft) ?? null;

	const { data: tags } = useGetTagsQuery();
	const [update, { isLoading }] = useUpdateCoursesMutation();
	const [currentFormState, setCurrentFormState] = useState<UpdateCourses>();
	const [thumbnailImageFileInfo, setThumbnailImageFileInfo] = useState<IFileInfo | null>(null);
	const [trailerVideoFileInfo, setTrailerFileInfo] = useState<IFileInfo | null>(null);
	const [largeImageFileInfo, setLargeImageFileInfo] = useState<IFileInfo | null>(null);
	const [currentShowingContentInfo, setCurrentShowingContentInfo] =
		useState<IStageContentInfo | null>(null);
	const [nodeToSelectAfterRender, setNodeToSelectAfterRender] = useState<Node<PhaseData> | null>(
		null
	);

	const [selectedNode, setSelectedNode] = useState<Node | null>(null);
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

	const [files, setFiles] = useState<IFileInfo[]>([]);

	const handleDeletePhase = () => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newContent = currentFormState?.json?.Content?.filter((c: any) => {
			return c.id !== +(selectedNode?.id?.replace("n", "") ?? 0);
		});

		deleteElements({ nodes: [selectedNode as Node<PhaseData>] });
		// const nodes = getNodes();
		handleSaveFormState(
			{
				...currentFormState?.json,
				Content: newContent,
			},
			"json"
		); // console.log(nodes, "nodes");

		handlePaneClick?.();
	};

	useEffect(() => {
		// Both queries, not one: initialising from the course while the draft is still on
		// its way re-initialised moments later and stomped anything typed in between.
		// And a dirty form is never re-initialised at all — cache refreshes after version
		// mutations produce a fresh draft object, which is no reason to discard edits.
		if (data && !isFetching && !isLoadingVersions && (!dirtyRef.current || reloadPending.current)) {
			// Field by field rather than wholesale: the draft mirrors the content columns and
			// nothing else, so the scenario stays the source for everything around it — its
			// files, its tag, its author.
			const draftJson = draft?.json as Record<string, unknown> | undefined;
			const source = draft ?? data;
			const json = (draftJson ?? data.json) as Record<string, unknown>;
			const formState = {
				id: +id,
				name: source.name ?? data.name,
				description: source.description ?? data?.description,
				forWhom: source.forWhom ?? data?.forWhom,
				language: source.language ?? data?.language,
				json: json && Object.keys(json).length ? json : EMPTY_COURSE_SCENARIO,
				tag: data?.tag,
				author: data?.author,
			};

			setCurrentFormState({
				...formState,
			});
			setBaseRevision(draft?.revision ?? 0);

			if (reloadPending.current) {
				// "Neu laden": the newer draft becomes both the values and what counts as unchanged.
				reloadPending.current = false;
				reset({
					name: formState.name,
					description: formState.description,
					forWhom: formState.forWhom,
					language: formState.language,
					json: formState.json,
					tag: formState.tag,
				});
				setEditorKey((key) => key + 1);
			}

			if (data?.courseFiles?.length) {
				const { contentFiles, largeImage, thumbnail, trailer } = data.courseFiles.reduce(
					(acc, item) => {
						if (item.fileAssignment === FileAssignment.Content) {
							acc.contentFiles.push(item);
						} else if (item.fileAssignment === FileAssignment.LargeImage) {
							acc.largeImage = item;
						} else if (item.fileAssignment === FileAssignment.Thumbnail) {
							acc.thumbnail = item;
						} else if (item.fileAssignment === FileAssignment.TrailerVideo) {
							acc.trailer = item;
						}

						return acc;
					},
					{
						contentFiles: [] as typeof data.courseFiles,
						largeImage: null as (typeof data.courseFiles)[number] | null,
						thumbnail: null as (typeof data.courseFiles)[number] | null,
						trailer: null as (typeof data.courseFiles)[number] | null,
					}
				);

				setFiles(contentFiles);
				setLargeImageFileInfo(largeImage);
				setThumbnailImageFileInfo(thumbnail);
				setTrailerFileInfo(trailer);
			}

			localStorage.removeItem(`scenario-draft-${id}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, draft, isFetching, isLoadingVersions, reloadNonce]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof UpdateCourses) => {
		setCurrentFormState((prevState) => ({
			...prevState,
			[name]: value,
		}));

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const handleNodeConnection = (nodeToSelect: Node<PhaseData>, newGraphData: ScenarioGraphData) => {
		// 1. Das Update der Formulardaten bleibt gleich

		handleSaveFormState(newGraphData, "json");

		// 2. Anstatt direkt zu selektieren, speichern wir den Knoten für später
		setNodeToSelectAfterRender(nodeToSelect);
	};

	useEffect(() => {
		// Prüfen, ob ein Knoten zur Auswahl "wartet"
		if (nodeToSelectAfterRender) {
			// 1. Den Knoten jetzt WIRKLICH auswählen
			setSelectedNode(nodeToSelectAfterRender);

			// 2. WICHTIG: Den temporären Zustand sofort zurücksetzen,
			// damit dieser Effekt nicht in einer Endlosschleife läuft.
			setNodeToSelectAfterRender(null);
		}
	}, [nodeToSelectAfterRender]);

	const {
		setValue,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = useForm<UpdateCourses>({
		defaultValues: {
			name: currentFormState?.name,
			description: currentFormState?.description,
			forWhom: currentFormState?.forWhom,
			language: currentFormState?.language,
			json: currentFormState?.json || EMPTY_COURSE_SCENARIO,
			tag: currentFormState?.tag,
		},
		resolver: zodResolver(UpdateCoursesSchema),
	});

	if (Object.keys(errors).length > 0) {
		console.error("errors", errors);
	}

	useEffect(() => {
		if (currentFormState) {
			setValue("name", currentFormState.name, { shouldDirty: false });
			setValue("description", currentFormState.description, { shouldDirty: false });
			setValue("forWhom", currentFormState.forWhom, { shouldDirty: false });
			setValue("language", currentFormState.language, { shouldDirty: false });
			setValue("json", currentFormState.json, { shouldDirty: false });
			setValue("tag", currentFormState.tag, { shouldDirty: false });
		}
	}, [currentFormState, setValue]);

	useEffect(() => {
		dirtyRef.current = isDirty;
	}, [isDirty]);

	// Warn on browser close/refresh if unsaved changes
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isDirty]);

	/** Saves; `overwrite` drops the revision check after the author chose to (stale dialog). */
	const onSubmit = async (values: UpdateCourses, overwrite = false): Promise<boolean> => {
		// if (Object.keys(errors).length > 0) {
		// 	return;
		// }

		// if (!isDirty) {
		// 	return;
		// }

		try {
			const courseFiles = [
				...files.map((fileInfo) => ({
					id: fileInfo.id,
					fullFilePath: fileInfo.fullFilePath,
					fileName: fileInfo.fileName,
					fileLength: fileInfo.fileLength,
					fileType: fileInfo.fileType,
					fileAssignment: fileInfo.fileAssignment,
					hash: fileInfo.hash,
				})),
				...(thumbnailImageFileInfo ? [thumbnailImageFileInfo] : []),
				...(trailerVideoFileInfo ? [trailerVideoFileInfo] : []),
				...(largeImageFileInfo ? [largeImageFileInfo] : []),
			];

			const currentJson = (typeof currentFormState?.json !== "string"
				? JSON.stringify(currentFormState?.json || {})
				: currentFormState?.json || "{}") as unknown as ScenarioGraphData;

			await update({
				...values,
				name: values?.name ? values.name : currentFormState?.name,
				tag: values?.tag ? values.tag : currentFormState?.tag,
				description: values?.description ?? currentFormState?.description,
				forWhom: values?.forWhom ?? currentFormState?.forWhom,
				author: values?.author ?? currentFormState?.author,
				json: currentJson,
				id: currentFormState?.id,
				courseFiles: courseFiles,
				...(overwrite ? {} : { draftRevision: baseRevision }),
			}).unwrap();

			localStorage.removeItem(`scenario-draft-${id}`);
			reset(values, { keepValues: false });

			const fresh = await refetchVersions();

			setBaseRevision(fresh.data?.find((version) => version.isDraft)?.revision ?? 0);

			return true;
		} catch (err) {
			const refusal = err as { status?: number; data?: { code?: string } };

			if (refusal?.status === 409 && refusal.data?.code === "STALE_DRAFT") {
				setStaleSave(values);
			} else {
				console.error(err);
			}

			return false;
		}
	};

	/** The stale dialog's "Neu laden": drops the own edits and shows the newer draft. */
	const reloadDraft = async () => {
		setStaleSave(null);
		reloadPending.current = true;
		localStorage.removeItem(`scenario-draft-${id}`);
		await Promise.all([refetch(), refetchVersions()]);
		setReloadNonce((nonce) => nonce + 1);
	};

	/** The stale dialog's "Trotzdem speichern": the author overwrites the newer draft on purpose. */
	const overwriteDraft = async () => {
		const values = staleSave;

		setStaleSave(null);

		if (values) {
			await onSubmit(values, true);
		}
	};

	const handleClose = () => {
		if (isDirty) {
			setShowUnsavedChangesModal(true);

			return;
		}

		localStorage.removeItem(`scenario-draft-${id}`);
		navigate(-1);
	};

	const handleDiscardAndClose = () => {
		setShowUnsavedChangesModal(false);
		localStorage.removeItem(`scenario-draft-${id}`);
		reset(undefined, { keepValues: true }); // clears isDirty without changing values
		if (blocker.state === "blocked") {
			blocker.proceed?.();
		} else {
			navigate(-1);
		}
	};
	const handleSetTags = (value?: AutoCompleteItem) => {
		if (value && value?.title) {
			handleSaveFormState(
				{
					id: value?.id ?? -1,
					name: value.title,
				} as CourseTag,
				"tag"
			);
		}
	};

	const blocker = useBlocker(isDirty);

	useEffect(() => {
		if (blocker.state === "blocked") {
			setShowUnsavedChangesModal(true);
		}
	}, [blocker.state]);

	const handleNodeClick = (event: React.MouseEvent, node: Node) => {
		setSelectedNode(node);
	};

	const handlePaneClick = () => {
		setSelectedNode(null);
	};

	// 1. Stack links bei Update Szenario, 2. Rechts, 3. Unten mit den 2 Knoepfen
	return (
		<>
			<div className="flex flex-col h-[calc(100vh-97px)]">
				<div className="flex flex-col md:flex-row w-full h-[calc(100%-80px)] bg-brand-white p-6 gap-8 border border-border rounded-md shadow-custom-xs">
					<Stack
						className="w-full md:w-[30%] h-full overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-gray-600 pr-4"
						spacing={"12px"}
					>
						{/* MODIFIED HERE */}
						{selectedNode ? (
							<PhaseEditor
								key={selectedNode.id}
								courseLanguage={currentFormState?.language}
								handleDeletePhase={handleDeletePhase}
								handlePaneClick={handlePaneClick}
								node={selectedNode as Node<PhaseData>}
							/>
						) : (
							<div className="flex items-center gap-2 py-3 px-4 text-gray-400">
								<svg
									className="w-5 h-5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.5}
									viewBox="0 0 24 24"
								>
									<path
										d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
								<p className="text-xs">{t("editor.emptyStateHint")}</p>
							</div>
						)}
						<div className={selectedNode ? "hidden" : "flex flex-col gap-1 w-full"}>
							{/* General course editing fields like name, description, tags, image uploaders, etc. */}
							<div className="flex flex-col gap-1 w-full">
								<InputField
									containerClassName="w-full md:w-3/3"
									error={!!errors.name?.message}
									errorMessage={errors.name?.message}
									handleChange={(e) => {
										const newName = e.target.value;

										handleSaveFormState(newName, "name");
									}}
									label={ts("name") + "*"}
									name={"name"}
									placeholder={ts("name")}
									showError={!!errors.name?.message}
									value={currentFormState?.name || ""}
								/>
								<AutoComplete
									data={
										tags
											?.filter((tag) => !!tag?.id && !!tag?.name)
											?.map((tag) => ({ id: tag?.id ?? -1, title: tag?.name ?? "" })) ?? []
									}
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									handleSearch={() => {
										/* Implement if needed */
									}}
									handleSelect={handleSetTags}
									label={ts("tags")}
									selectedItem={currentFormState?.tag?.name ?? ""}
								/>
								<LanguageSelect
									currentLanguage={
										(currentFormState?.language?.toUpperCase() as UserLanguage) ?? "DE"
									}
									handleLanguageChange={(lang: UserLanguage) => {
										handleSaveFormState(lang, "language");
									}}
									languages={languages}
								/>
							</div>

							{/* AUTHOR FIELD – direkt nach Tags */}
							<InputField
								containerClassName="w-full"
								error={!!errors.author?.message}
								handleChange={(e) => {
									const newAuthor = e.target.value;

									handleSaveFormState(newAuthor, "author");
								}}
								label={ts("author")}
								name="author"
								placeholder={ts("author")}
								value={currentFormState?.author || ""}
							/>
							<InputField
								className={`bg-white h-auto !overflow-hidden`}
								errorMessage={errors.description?.message}
								handleChange={(e) => handleSaveFormState(e.target.value, "description")}
								label={ts("description")}
								multiline={true}
								name={"description"}
								placeholder={ts("description")}
								showError={!!errors.description?.message}
								value={currentFormState?.description || ""}
							/>
							<InputField
								className={`bg-white h-auto !overflow-hidden`}
								errorMessage={errors.forWhom?.message}
								handleChange={(e) => handleSaveFormState(e.target.value, "forWhom")}
								label={ts("forWhom")}
								multiline={true}
								name={"forWhom"}
								placeholder={ts("forWhom")}
								showError={!!errors.forWhom?.message}
								value={currentFormState?.forWhom || ""}
							/>
							<ImageUploader
								border
								aspectRatio={310 / 183}
								courseId={+id}
								currentImgFileInfo={thumbnailImageFileInfo}
								description={t("basics.thumbnail-description")}
								fileAssignment={FileAssignment.Thumbnail}
								imagePrefix="course-thumbnail"
								label={t("basics.thumbnail")}
								maxFileSizeInBytes={2 * 1024 * 1024}
								setCurrentContentInfo={setCurrentShowingContentInfo}
								setCurrentImgFileInfo={setThumbnailImageFileInfo}
							/>
							<ImageUploader
								border
								aspectRatio={1330 / 459}
								courseId={+id}
								currentImgFileInfo={largeImageFileInfo}
								description={t("basics.large-image-description")}
								fileAssignment={FileAssignment.LargeImage}
								imagePrefix="course-large-image"
								label={t("basics.large-image")}
								maxFileSizeInBytes={2 * 1024 * 1024}
								setCurrentContentInfo={setCurrentShowingContentInfo}
								setCurrentImgFileInfo={setLargeImageFileInfo}
							/>
							<VideoUploader
								border
								courseId={+id}
								currentVideoFileInfo={trailerVideoFileInfo} // Ensure these state variables are correctly managed
								fileAssignment={FileAssignment.TrailerVideo}
								label={t("basics.trailer-video")}
								maxFileSizeInBytes={500 * 1024 * 1024}
								setCurrentContentInfo={setCurrentShowingContentInfo} // Ensure these props are correctly used
								setCurrentVideoFileInfo={setTrailerFileInfo}
								videoPrefix="course-trailer-video"
							/>
						</div>
					</Stack>

					{/* Right Panel: graph, intro-video assistant and the AI job/cost log */}
					<Stack className="w-full md:w-[70%] space-y-4">
						{/*
						 * `isLoading`, not `isFetching`: the first is the initial load, the second
						 * is every re-read — and publishing ends in one. Tearing the panel down
						 * for it discarded which tab was open, so publishing dropped the author
						 * back to Flow, and took the graph's pan and zoom with it, which is the
						 * very thing this panel keeps the graph mounted to protect.
						 */}
						{data && !isLoadingCourse && !isLoadingVersions && currentFormState?.json && (
							<EditorRightPanel
								courseId={+id}
								graph={
									<CustomJsonEditor
										key={editorKey}
										containerClassName="!h-[100%]"
										courseId={+id}
										editorsClassName="!min-h-[85%]"
										externalErrorMessage={(errors?.json?.message as string) || ""}
										value={{
											...currentFormState?.json,
											scenarioName: currentFormState?.name,
										}}
										onChange={(value) => {
											handleSaveFormState(value, "json");
										}}
										onConnectionNodeSelect={handleNodeConnection}
										onNodeClick={handleNodeClick}
										onPaneClick={handlePaneClick}
									/>
								}
								onViewVersion={handleViewVersion}
							/>
						)}
					</Stack>
				</div>

				{/*
				 * Two groups rather than one row with an auto margin: publishing on the left,
				 * away from Save, because those are the only controls here that change what
				 * other people see. An auto margin inside a Stack that sets its own child
				 * margins is one cascade change away from collapsing back into the row.
				 */}
				<div className="mt-2 flex flex-wrap items-center justify-between gap-4 px-4">
					<div className="flex flex-wrap items-center gap-4">
						{!!publishing.error && (
							<span className="rounded-card bg-error-light px-3 py-1.5 text-xs font-semibold text-text">
								{publishing.error}
							</span>
						)}
					</div>

					<div className="flex flex-wrap items-center gap-3">
						{/*
						 * One slot, two states, never both: while there are unsaved edits that is
						 * the thing to do next, and only once they are saved does "not published
						 * yet" become the thing to do next. Two badges at once would have asked
						 * for two actions when only one is available.
						 */}
						{/*
						 * Beside the state it qualifies rather than across the bar from it: the
						 * hint below names which version is live, which is what this badge shows.
						 */}
						{!!id && <PublishStatus status={data?.status} />}

						{isDirty ? (
							<span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-600">
								{t("editor.unsavedChanges")}
							</span>
						) : publishing.isActive && publishing.hasDraft ? (
							// Only while the scenario is live. Unpublished work on something that was
							// never published is what "Entwurf" beside it already says, and the pair
							// would be one sentence twice. Live-with-newer-work is the case worth a
							// second badge.
							//
							// The app's own tooltip rather than a `title`: that one waits a second,
							// renders in the OS chrome and is easy to miss entirely.
							<Tooltip content={publishing.editingHint}>
								<span className="cursor-help rounded-full bg-warning-light px-3 py-1 text-xs font-medium text-warning-ink">
									{t("editor.publish.unpublishedChanges")}
								</span>
							</Tooltip>
						) : null}

						{publishing.isActive && (
							<Button
								className="!h-[36px] !w-auto"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={publishing.isBusy}
								title={t("editor.publish.unpublish")}
								type="button"
								onClick={publishing.askUnpublish}
							></Button>
						)}
						<Button
							className="!h-[36px] !w-auto"
							color={ButtonColor.ACTION_SECONDARY}
							title={t("basics.cancel")}
							onClick={handleClose}
						></Button>
						{/* Nothing to save is worth showing as a disabled button rather than a live
						    one that writes an identical draft and moves the state on for nothing. */}
						<Button
							className="!h-[36px] !w-auto"
							color={ButtonColor.ACTION}
							disabled={!isDirty || Object.keys(errors).length > 0}
							title={t("basics.saveAndClose")}
							onClick={() =>
								handleSubmit(async (data) => {
									if (await onSubmit(data)) {
										navigate(-1);
									}
								})()
							}
						></Button>
						<Button
							className="!h-[36px] !w-auto"
							color={ButtonColor.ACTION}
							disabled={!isDirty || isLoading || Object.keys(errors).length > 0}
							isLoading={isLoading}
							title={t("basics.save")}
							onClick={handleSubmit((values) => onSubmit(values))}
						></Button>
						{/*
						 * Hidden once the scenario is live *and* has nothing waiting: publishing
						 * again would be a no-op. It comes back the moment a save creates a draft,
						 * which is exactly when the indicator beside it says to press it.
						 */}
						{/*
						 * Not while there are unsaved edits. Publishing puts the *saved* draft
						 * live, so offering it next to an editor showing something else invites
						 * pushing the thing you are not looking at — most obviously right after
						 * loading an old version. Save first; then it means what it says.
						 */}
						{!isDirty && (!publishing.isActive || publishing.hasDraft) && (
							<Button
								className="!h-[36px] !w-auto"
								color={ButtonColor.ACTION}
								disabled={publishing.isBusy || !publishing.canPublish}
								isLoading={publishing.isPublishing}
								title={t("editor.publish.publish")}
								type="button"
								onClick={publishing.askPublish}
							></Button>
						)}
					</div>
				</div>
			</div>

			{publishing.dialogs}

			<ContentModal
				contentInfo={currentShowingContentInfo}
				setCurrentContentInfo={setCurrentShowingContentInfo}
			/>
			{staleSave && (
				<Modal
					cancelButtonText={t("editor.staleDraft.keepEditing")}
					handleClose={() => setStaleSave(null)}
					handleSave={reloadDraft}
					handleSecondSave={overwriteDraft}
					isOpened={!!staleSave}
					secondSubmitButtonText={t("editor.staleDraft.overwrite")}
					size="md"
					submitButtonText={t("editor.staleDraft.reload")}
					title={t("editor.staleDraft.title")}
				>
					<p data-testid="stale-draft">{t("editor.staleDraft.text")}</p>
				</Modal>
			)}
			{showUnsavedChangesModal && (
				<Modal
					handleClose={() => {
						setShowUnsavedChangesModal(false);
						blocker.reset?.();
					}}
					handleSave={handleDiscardAndClose}
					isOpened={showUnsavedChangesModal}
					submitButtonColor="error"
					submitButtonText={t("editor.discardChanges")}
					title={t("editor.unsavedChanges")}
				>
					<p>{t("editor.unsavedChangesWarning")}</p>
				</Modal>
			)}
		</>
	);
};
